import { useEffect, useRef, useState } from "react"
import { HStack, Spacer, Stack } from "@chakra-ui/react"
import { createPlaidLinkToken, submitPlaidPayment } from "@/api"
import { Button, Text } from "@/components"
import { ensurePaymentsClient, getApiErrorMessage } from "../utils"

interface Props {
  defaultClientData: {
    firstName: string
    lastName: string
    email: string
    companyName: string
    addressLine1?: string
    city?: string
    state?: string
    zip?: string
    country?: string
  }
  paymentAmount: number
  paymentLabel: string
}

export const UsPlaidWireInformationComponent = ({ defaultClientData, paymentAmount, paymentLabel }: Props) => {
  const [isInitializing, setIsInitializing] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [linkToken, setLinkToken] = useState("")
  const [message, setMessage] = useState("")
  const [messageType, setMessageType] = useState<"info" | "error" | "success">("info")
  const [isPlaidLoaded, setIsPlaidLoaded] = useState(false)
  const clientIdRef = useRef<string>("")
  const plaidHandlerRef = useRef<PlaidHandler | null>(null)
  const hasInitialized = useRef(false)

  const showMessage = (msg: string, type: "info" | "error" | "success" = "info") => {
    setMessage(msg)
    setMessageType(type)
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
    }).format(amount)
  }

  const initializePlaid = async () => {
    showMessage("")
    setIsInitializing(true)
    try {
      if (!defaultClientData.firstName || !defaultClientData.lastName || !defaultClientData.email || !defaultClientData.companyName) {
        throw new Error("Missing required client information (name, email, or company).")
      }

      const paymentsClient = await ensurePaymentsClient({
        first_name: defaultClientData.firstName,
        last_name: defaultClientData.lastName,
        email: defaultClientData.email,
        company_name: defaultClientData.companyName,
        address_line1: defaultClientData.addressLine1,
        city: defaultClientData.city,
        state: defaultClientData.state,
        zip: defaultClientData.zip,
        country: defaultClientData.country || "US",
      })

      clientIdRef.current = paymentsClient.id

      const linkTokenResponse = await createPlaidLinkToken({
        client_id: paymentsClient.id,
      })
      setLinkToken(String(linkTokenResponse.link_token || ""))
      showMessage("Bank connection ready. Click Connect Bank to proceed.", "info")
    } catch (error) {
      showMessage(getApiErrorMessage(error), "error")
    } finally {
      setIsInitializing(false)
    }
  }

  const completePlaidPayment = async (publicToken: string, accountId: string) => {
    showMessage("")
    setIsSubmitting(true)
    try {
      if (!paymentAmount || paymentAmount <= 0) {
        throw new Error("Payment amount has not been determined.")
      }

      const result = await submitPlaidPayment({
        client_id: clientIdRef.current,
        public_token: publicToken,
        account_id: accountId,
        amount: paymentAmount,
      })
      showMessage(
        `Payment submitted successfully. Transaction ID: ${result.transaction?.id ?? "pending"}. Approval emails have been sent automatically.`,
        "success",
      )
    } catch (error) {
      showMessage(getApiErrorMessage(error), "error")
    } finally {
      setIsSubmitting(false)
    }
  }

  // Load Plaid SDK
  useEffect(() => {
    if (window.Plaid) {
      setIsPlaidLoaded(true)
      return
    }

    const existingScript = document.getElementById("plaid-link-sdk")
    if (existingScript) {
      existingScript.addEventListener("load", () => setIsPlaidLoaded(true))
      return
    }

    const script = document.createElement("script")
    script.id = "plaid-link-sdk"
    script.src = "https://cdn.plaid.com/link/v2/stable/link-initialize.js"
    script.async = true
    script.onload = () => setIsPlaidLoaded(true)
    script.onerror = () => showMessage("Failed to load Plaid Link SDK.", "error")
    document.body.appendChild(script)
  }, [])

  // Auto-initialize Plaid link token once we have required data
  useEffect(() => {
    if (
      !hasInitialized.current
      && defaultClientData.firstName
      && defaultClientData.email
      && paymentAmount > 0
    ) {
      hasInitialized.current = true
      initializePlaid()
    }
  }, [defaultClientData, paymentAmount])

  // Create Plaid handler when link token is ready
  useEffect(() => {
    if (!isPlaidLoaded || !linkToken || !window.Plaid) {
      return
    }

    if (plaidHandlerRef.current) {
      plaidHandlerRef.current.destroy()
      plaidHandlerRef.current = null
    }

    plaidHandlerRef.current = window.Plaid.create({
      token: linkToken,
      onSuccess: (publicToken: string, metadata: PlaidMetadata) => {
        const accountId =
          metadata?.account_id ||
          (metadata?.accounts && metadata.accounts[0]?.id) ||
          ""
        if (!accountId) {
          showMessage("No bank account was selected. Please try again.", "error")
          return
        }
        void completePlaidPayment(publicToken, accountId)
      },
      onExit: (error: { error_message?: string } | null) => {
        if (error) {
          showMessage("Plaid Link exited with an error. Please retry.", "error")
        }
      },
      onEvent: () => {},
    })


    return () => {
      if (plaidHandlerRef.current) {
        plaidHandlerRef.current.destroy()
        plaidHandlerRef.current = null
      }
    }
  }, [isPlaidLoaded, linkToken])

  const openPlaidLink = () => {
    if (!paymentAmount || paymentAmount <= 0) {
      showMessage("Payment amount has not been determined.", "error")
      return
    }
    if (!plaidHandlerRef.current) {
      showMessage("Plaid is not ready yet. Please wait a moment.", "error")
      return
    }
    plaidHandlerRef.current.open()
  }

  const messageColor = messageType === "error" ? "red.600" : messageType === "success" ? "green.600" : "gray.600"

  return (
    <Stack w="100%" bg="white" gap={5}>
      <Text fontSize="md">
        Connect your US bank account through Plaid to securely provide payment details.
      </Text>
      <Text fontSize="sm" color="gray.600">
        Your bank credentials are handled securely by Plaid and never stored on our servers.
      </Text>

      <Stack p={5} borderWidth="1px" borderRadius="md" borderColor="gray.200" bg="gray.50">
        <Text fontWeight="bold">How it works</Text>
        <Text fontSize="sm">1. Review the payment details below.</Text>
        <Text fontSize="sm">2. Click Connect Bank to open the Plaid window.</Text>
        <Text fontSize="sm">3. Select your US checking account (ideally the one connected to your equity portal, e.g. Carta).</Text>
        <Text fontSize="sm">4. Payment is submitted for approval automatically.</Text>
        <Text fontSize="xs" color="gray.600">
          Plaid SDK: {isPlaidLoaded ? "Ready" : "Loading..."} | Session: {isInitializing ? "Initializing..." : linkToken ? "Ready" : "Pending"}
        </Text>
      </Stack>

      <Stack p={5} borderWidth="1px" borderRadius="lg" borderColor="blue.200" bg="blue.50">
        <Text fontSize="sm" color="gray.600">{paymentLabel}</Text>
        <Text fontSize="2xl" fontWeight="bold">{formatCurrency(paymentAmount)}</Text>
      </Stack>

      <HStack>
        <Spacer />
        <Button
          variant="solid"
          w="fit-content"
          disabled={!isPlaidLoaded || !linkToken || isSubmitting || paymentAmount <= 0}
          onClick={openPlaidLink}
        >
          {isSubmitting ? "Connecting..." : "Connect Bank"}
        </Button>
      </HStack>
      {message ? <Text fontSize="sm" color={messageColor}>{message}</Text> : null}
    </Stack>
  )
}

type PlaidHandler = {
  open: () => void
  destroy: () => void
}

type PlaidAccount = {
  id: string
  name?: string
  mask?: string
  type?: string
  subtype?: string
}

type PlaidMetadata = {
  account_id?: string
  accounts?: PlaidAccount[]
  institution?: { name: string; institution_id: string }
}

type PlaidFactory = {
  create: (config: {
    token: string
    onSuccess: (publicToken: string, metadata: PlaidMetadata) => void
    onExit?: (error: { error_message?: string } | null) => void
    onEvent?: (eventName: string) => void
  }) => PlaidHandler
}

declare global {
  interface Window {
    Plaid?: PlaidFactory
  }
}
